import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "../component/Header";
import { FiSearch, FiX, FiChevronDown } from "react-icons/fi";
import { API_BASE_URL } from "../utils/apiConfig";

function TransactionPage() {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const userRole = localStorage.getItem("role");
    const adminDeviceId = localStorage.getItem("device_id");

    const fetchTransactions = useCallback(async (deviceId) => {
        setLoading(true);
        setError(null);
        try {
            let url = `${API_BASE_URL}/api/transaction-history`;

            if (deviceId) {
                url += `?device_id=${deviceId}`;
            } else if (userRole === "admin") {
                if (!adminDeviceId) {
                    setError("Device ID not found. Please log in again.");
                    setLoading(false);
                    return;
                }
                url += `?device_id=${adminDeviceId}`;
            }

            const res = await axios.get(url);
            if (res.data.success) {
                setTransactions(res.data.data || []);
            } else {
                setTransactions([]);
                setError("Failed to load transactions.");
            }
        } catch (err) {
            console.error(err);
            setError("Error fetching transactions.");
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [userRole, adminDeviceId]);

    useEffect(() => {
        const init = async () => {
            try {
                const token = localStorage.getItem("token");
                const config = { headers: { Authorization: `Bearer ${token}` } };
                // API returns only assigned devices for admin, all for superadmin
                const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
                setDevices(devicesRes.data);

                let initialId = "";
                if (userRole !== "superadmin") {
                    initialId = devicesRes.data?.[0]?.device_id_PK || "";
                }

                setSelectedDeviceId(initialId);
                fetchTransactions(initialId);
            } catch (err) {
                console.error("Failed to fetch devices", err);
                fetchTransactions("");
            }
        };
        init();
    }, [fetchTransactions]);

    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        fetchTransactions(id);
    };

    const displayedTransactions = transactions.filter(txn => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const flavorsText = txn.flavors?.map(f => f.name).join(" ");
        return (
            String(txn.transaction_id)?.toLowerCase().includes(query) ||
            txn.device_name?.toLowerCase().includes(query) ||
            txn.base_name?.toLowerCase().includes(query) ||
            flavorsText?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            <div className="grid grid-cols-1 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
                        <div>
                            <h2 className="text-xl font-semibold text-green-400">Transaction History</h2>
                            <p className="text-xs text-gray-500 mt-1">
                                Displaying {displayedTransactions.length} record{displayedTransactions.length !== 1 ? "s" : ""}
                            </p>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
                            {/* Device Selector */}
                            {devices.length > 0 && (
                                <div className="relative flex items-center h-9 bg-black/40 rounded-xl border border-white/5 pl-3 pr-2 transition focus-within:ring-1 focus-within:ring-green-500">
                                    <select
                                        value={selectedDeviceId}
                                        onChange={handleDeviceChange}
                                        className="bg-transparent text-gray-300 text-[11px] font-bold outline-none cursor-pointer appearance-none pr-6 w-28 md:w-36 truncate"
                                    >
                                        {userRole === "superadmin" && <option value="" className="bg-[#1b1d1a] text-white">All Devices</option>}
                                        {devices.map(device => (
                                            <option key={device.device_id_PK} value={device.device_id_PK} className="bg-[#1b1d1a] text-white">
                                                {device.device_name}
                                            </option>
                                        ))}
                                    </select>
                                    <FiChevronDown size={11} className="absolute right-3 text-gray-600 pointer-events-none" />
                                </div>
                            )}

                            {/* Search bar */}
                            <div className="relative flex items-center h-9">
                                <FiSearch size={13} className="absolute left-3 text-gray-500" />
                                <input
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="h-full bg-black/40 border border-white/5 text-gray-300 text-[11px] font-bold rounded-xl pl-8 pr-8 focus:outline-none focus:ring-1 focus:ring-green-500 transition w-36 md:w-48 placeholder:text-gray-600"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-3 text-gray-500 hover:text-red-400 transition"
                                    >
                                        <FiX size={13} />
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={() => fetchTransactions(selectedDeviceId)}
                                className="flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 text-[11px] font-black uppercase tracking-widest px-4 h-9 rounded-xl transition shrink-0"
                            >
                                ↻ Refresh
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="text-center py-8 text-gray-400">Loading transactions...</div>
                    ) : error ? (
                        <div className="text-center py-8 text-red-400">{error}</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-gray-300">
                                <thead>
                                    <tr className="border-b border-gray-700 text-gray-400 text-left">
                                        <th className="px-4 py-3">Sr.</th>
                                        <th className="px-4 py-3">Transaction ID</th>
                                        <th className="px-4 py-3">Device</th>
                                        <th className="px-4 py-3">Base Product</th>
                                        <th className="px-4 py-3">Flavors</th>
                                        <th className="px-4 py-3">Amount</th>
                                        <th className="px-4 py-3">Date & Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedTransactions.map((txn, index) => (
                                        <tr key={txn.id} className="border-b border-gray-800 hover:bg-[#1e2a1e] transition">
                                            <td className="px-4 py-3 font-medium text-white">{index + 1}</td>
                                            <td className="px-4 py-3 text-green-300 font-mono text-xs">{txn.transaction_id}</td>
                                            <td className="px-4 py-3">
                                                <div className="text-white font-medium">{txn.device_name || "—"}</div>
                                                <div className="text-gray-500 text-xs">ID: {txn.device_id}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {txn.base_image && (
                                                        <img src={txn.base_image} alt={txn.base_name} className="w-8 h-8 rounded-md object-cover border border-gray-700" />
                                                    )}
                                                    <span className="text-gray-200">{txn.base_name || "—"}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {txn.flavors && txn.flavors.length > 0 ? (
                                                        txn.flavors.map((f) => (
                                                            <div key={f.flavors_id_PK} className="flex items-center gap-1 bg-black/40 border border-gray-700 rounded-full px-2 py-0.5">
                                                                {f.image && <img src={f.image} alt={f.name} className="w-4 h-4 rounded-full object-cover" />}
                                                                <span className="text-xs text-gray-300">{f.name}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <span className="text-gray-600 text-xs">—</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-green-400 font-semibold">₹{txn.amount}</td>
                                            <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{new Date(txn.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {displayedTransactions.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500 italic">No transactions found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TransactionPage;
